import type { MarketDataSnapshot } from '../../../types/MarketDataSnapshot';
import type { PriceInputMode } from '../PriceQuickOptions';

interface IsQuickOptionDisabledParams {
  optionValue: string;
  marketData?: MarketDataSnapshot;
  priceInputMode?: PriceInputMode;
  priceValue?: string;
}

/**
 * Determines if a given quick option should be disabled based on the market data, price input mode, and current price value.
 * @param optionValue - The value of the option to check (e.g., 'bid', 'offer', '0.001', '-0.001').
 * @param marketData - The current market data snapshot.
 * @param priceInputMode - The current price input mode ('Increment' or 'Reset').
 * @param priceValue - The current price value in the price input.
 * @returns {boolean} - True if the quick option should be disabled, false otherwise.
 */
export function isQuickOptionDisabled({
  optionValue,
  marketData,
  priceInputMode,
  priceValue,
}: IsQuickOptionDisabledParams): boolean {
  const hasBids = Boolean(marketData?.Bids?.length);
  const hasOffers = Boolean(marketData?.Offers?.length);
  const hasPriceValue = Boolean(priceValue);

  // Handle bid/offer buttons - they only depend on market data availability
  if (optionValue === 'bid') {
    return !hasBids;
  }
  if (optionValue === 'offer') {
    return !hasOffers;
  }

  // Handle BPS buttons (e.g., '+0.001', '-0.001')
  const isNegativeBps = optionValue.startsWith('-');

  // In Increment mode with an existing price, BPS buttons can always modify that price
  // regardless of market data (we're incrementing/decrementing the user's input, not market data)
  if (priceInputMode === 'Increment' && hasPriceValue) {
    return false;
  }

  // In Reset mode (or Increment mode without a price), we need market data to calculate from:
  // - Negative BPS buttons need bids (they calculate from the bid price)
  // - Positive BPS buttons need offers (they calculate from the offer price)
  return isNegativeBps ? !hasBids : !hasOffers;
}
